import React from "react";
import Screen from "../uizard/ui/Screen";

export default function Terms() {
  return (
    <Screen>
      <StyleTag />
      <div className="legal-wrap">
        <header className="legal-header">
          <h1 className="h1">Terms of Service</h1>
          <p className="lead">Effective Date: September 5, 2025 · Last reviewed: June 13, 2026</p>
          <p className="entity">Entity: <b>ArtiBytes</b> (“ArtiBytes,” “we,” “us,” “our”)</p>
          <p className="intro">
            By using ArtiBytes, you agree to these Terms and our{" "}
            <a href="/privacy">Privacy Policy</a> and{" "}
            <a href="/content-policy">Content Policy</a>.
          </p>
        </header>

        <section className="legal-section">
          <h2 className="h2">1) Service Overview &amp; Accounts</h2>
          <p>
            ArtiBytes provides AI-powered image generation, AI-powered video and animation
            generation, and a Creator program for training and hosting Styles. You must provide
            accurate information and keep your account secure. We may refuse, suspend, or
            terminate access for policy, safety, or legal reasons.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">2) Acceptable Use</h2>
          <p>You may not use ArtiBytes to:</p>
          <ul>
            <li>Violate laws or third-party rights (including IP, privacy, publicity).</li>
            <li>Upload illegal, harmful, deceptive, or exploitative content.</li>
            <li>Attempt to reverse engineer, misuse, or attack our services.</li>
            <li>Circumvent usage limits, fees, or safety systems.</li>
          </ul>
          <p>We may enforce limits and take action to ensure safety and compliance.</p>
        </section>

        <section className="legal-section">
          <h2 className="h2">3) User Content &amp; Generated Outputs</h2>
          <p>
            <b>Your uploads (User Content):</b> You retain ownership. You grant ArtiBytes a
            non-exclusive, worldwide, sublicensable, transferable license to host, process,
            and display your content to provide the service and enforce policies.
          </p>
          <p>
            <b>Generated outputs:</b> Subject to these Terms and applicable law, you own the
            images and videos you generate (to the extent permitted), and ArtiBytes grants you a
            non-exclusive, worldwide, royalty-free license to use, reproduce, display, and
            distribute your outputs for personal and commercial purposes.
          </p>
          <p>
            <b>AI-generated content notice:</b> All images and videos generated through ArtiBytes
            are produced by artificial intelligence. You are responsible for reviewing outputs
            before use or distribution and for complying with any applicable laws or platform
            policies regarding AI-generated content, including disclosure requirements.
          </p>
          <p>
            <b>Creator Styles:</b> If you generate with a Creator Style, your use of outputs
            is also subject to any style-specific restrictions disclosed in-app. Royalties
            from paid generations are handled under the Creator Program and Creator
            Agreement. ArtiBytes (not the Creator) owns the model weights and serving
            artifacts.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">4) Intellectual Property; ArtiBytes Materials</h2>
          <p>
            ArtiBytes and its licensors own all rights in the platform, models, trained
            weights, templates, and documentation (collectively, “ArtiBytes Materials”).
            Except for the rights expressly granted to you here, no other rights are granted
            or implied.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">5) Credits, Pi Transactions &amp; Refunds</h2>
          <ul>
            <li>Credits are priced in Pi and purchased through the Pi Network payment flow. The Pi amount at checkout is calculated against the live Pi market rate at the time of purchase.</li>
            <li>Once an image generation or video animation is successfully delivered, the credit spend is final.</li>
            <li>
              If a generation or animation fails due to a technical error on our side (e.g., model
              timeout, service interruption, AI processing failure), credits are automatically
              refunded to your in-app balance. Refunds are issued as credits, not as Pi. This
              applies to both image generations and video animations.
            </li>
            <li>
              If you believe you were charged in error (e.g., double-debited, undelivered
              generation or animation), contact support at{" "}
              <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a> within 7 days of
              the transaction. We will investigate and, where appropriate, restore credits to
              your balance.
            </li>
            <li>We do not issue Pi refunds to user wallets except in exceptional circumstances at our sole discretion.</li>
            <li>
              You are responsible for accurate wallet details. We are not liable for
              on-chain/network failures outside our control.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">6) Creator Program (Summary)</h2>
          <p>
            If you submit a style pack, the ArtiBytes Creator Commercial Licensing Agreement
            applies. You must own or control all rights to your uploads. ArtiBytes may
            review, approve, suspend, or remove Styles for policy or safety reasons.
            Royalties accrue on paid generations and animations using your Style; payouts occur
            to your Pi wallet when thresholds are met and subject to program terms.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">7) Content Policy &amp; Reporting</h2>
          <p>
            Use must comply with our Content Policy (no infringing, explicit, violent, or
            sensitive content; no illegal use). This applies equally to image generation prompts,
            video/animation generation prompts, and uploaded source images. You can report
            concerns in-app or by email. We may remove content or restrict features at our
            discretion.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">8) Disclaimers</h2>
          <p>
            The service is provided “as is” and “as available.” We disclaim all warranties,
            express or implied, including merchantability, fitness for a particular purpose,
            and non-infringement. We do not guarantee uninterrupted or error-free operation,
            specific creative results, or that outputs will be unique or free from claims by
            others. AI-generated images and videos may contain inaccuracies, artefacts, or
            unexpected content; you use outputs at your own risk.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">9) Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, ArtiBytes’ aggregate liability arising
            out of or relating to the service is limited to the greater of (i) €50 or (ii)
            the total fees you paid to ArtiBytes in the 12 months before the event giving
            rise to liability. We are not liable for indirect, incidental, special,
            consequential, or punitive damages, or lost profits/revenue, even if advised of
            the possibility.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">10) Indemnity</h2>
          <p>
            You will defend, indemnify, and hold harmless ArtiBytes and its personnel from
            any claims, damages, liabilities, and costs (including reasonable legal fees)
            arising from your content, your use of the service, or your breach of these
            Terms or applicable law.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">11) Termination</h2>
          <p>
            You may stop using ArtiBytes at any time. We may suspend or terminate access if
            you violate these Terms, the Content Policy, or law, or for security/compliance
            reasons. Provisions that by their nature should survive (e.g., IP ownership,
            disclaimers, limitations, indemnity, governing law) do survive termination.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">12) Changes</h2>
          <p>
            We may modify these Terms. We’ll post updates in-app and adjust the Effective
            Date. Material changes will be notified in-app (and by email where available).
            Continued use after changes means you accept the updated Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">13) Governing Law &amp; Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of Estonia without regard to
            conflict-of-law rules. Courts located in Estonia will have exclusive
            jurisdiction.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">14) Contact</h2>
          <p>
            Questions or concerns:{" "}
            <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a>
          </p>
        </section>
      </div>
    </Screen>
  );
}

/* ------------------------------ Styles ------------------------------ */
function StyleTag() {
  return (
    <style>{`
.legal-wrap{
  max-width: 860px;
  margin: 0 auto;
}
.legal-header .entity,
.legal-header .intro{
  color: #6B7280;
}
.legal-header .intro a{
  color: #6D5EF9; text-decoration: none; font-weight: 700;
}
.legal-header .intro a:hover{ text-decoration: underline; }
.legal-section{ margin-top: 18px; }
.legal-section p{ margin: 8px 0; line-height: 1.6; }
.legal-section ul{ margin: 8px 0 0 20px; display: grid; gap: 6px; }
    `}</style>
  );
}